import { TURNSTILE_SITEKEY } from '../helpers/constants';

/** Preloaded by public/index.html: both URLs must stay identical. */
const SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
/** Must match the action the API expects, otherwise a token minted here is refused there. */
const ACTION = 'session_token';
/**
 * How long a caller waits for a token - loading the script included - before its search is told
 * there is none. Kept short because the visitor is looking at a loading screen meanwhile: a slow
 * challenge is not lost, the widget keeps going and a late token still becomes a session (and
 * restarts the search). Only time the page is visible counts: a tab opened in the background does
 * not use up its budget before anyone looks at it.
 */
const SOLVE_TIMEOUT_MS = 8000;
/**
 * How long the widget keeps going once the caller has stopped waiting. Cloudflare keeps retrying,
 * and a token it produces now is still valid for five minutes: handed over, it saves the visitor's
 * next search from being refused. In production, about 230 solved challenges a day never reached
 * the API.
 */
const LATE_TOKEN_WINDOW_MS = 45000;
/**
 * Pause before Cloudflare retries a failed attempt. Its default, 8 s, would fit no retry in the
 * budget; a first attempt that fails typically does so after about 6 s.
 */
const RETRY_INTERVAL_MS = 2000;
/**
 * Cloudflare error codes that no retry can fix: a wrong or disabled sitekey, a domain the widget
 * does not allow, a visitor's clock or cache it cannot work with. Every other code - a suspected bot
 * (300*, 600*), an iframe that failed to load (200500), a challenge that took too long (110600) -
 * is worth waiting for Cloudflare's retry.
 * @see https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
 */
const FINAL_ERRORS = [
  '110100',
  '110110',
  '110200',
  '200100',
  '400020',
  '400070',
];

/** Whether the page stayed visible while the challenge ran: "mixed" when it was both. */
export type TurnstileVisibility = 'visible' | 'hidden' | 'mixed';

/**
 * How the challenge went, reported to the API with the token or the error. It is what tells a
 * challenge slower than we wait for from one stalled in a tab nobody was looking at.
 */
export type TurnstileTiming = {
  elapsedMs: number;
  visibility: TurnstileVisibility;
};

/**
 * The outcome of a challenge: a token, or why there is none. The reason is either the code
 * Cloudflare raised or one of ours below, and is reported to the API with the empty token - it is
 * the only way to tell a suspected bot from an ad blocker once the challenge has failed.
 */
export type TurnstileResult =
  | { token: string; error: null; timing: TurnstileTiming }
  | { token: null; error: string; timing: TurnstileTiming };

export type TurnstileOptions = {
  /**
   * Called with a token that only arrived after the result was reported as a failure, within
   * LATE_TOKEN_WINDOW_MS. Without it, the widget is removed as soon as the caller stops waiting.
   */
  onLateToken?: (token: string, timing: TurnstileTiming) => void;
};

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  retry: 'auto' | 'never';
  'retry-interval': number;
  callback: (token: string) => void;
  'error-callback': (errorCode: string) => boolean;
  'before-interactive-callback': () => void;
  'timeout-callback': () => void;
  'unsupported-callback': () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (container: HTMLElement) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type VisibleClock = {
  /** Resolves once the budget is spent, counting visible time only. */
  expired: Promise<void>;
  timing: () => TurnstileTiming;
  dispose: () => void;
};

const isHidden = (): boolean => document.visibilityState === 'hidden';

/**
 * @description A countdown that only runs while the page is visible, and that remembers whether
 * the page was visible, hidden, or both since it started.
 * @param {number} budgetMs
 * @returns {VisibleClock}
 */
const createVisibleClock = (budgetMs: number): VisibleClock => {
  const startedAt = Date.now();
  let remaining = budgetMs;
  let armedAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seenVisible = !isHidden();
  let seenHidden = isHidden();
  let expire: () => void = () => undefined;
  const expired = new Promise<void>((resolve) => {
    expire = resolve;
  });

  const arm = (): void => {
    armedAt = Date.now();
    timer = setTimeout(() => {
      timer = null;
      remaining = 0;
      expire();
    }, remaining);
  };

  const pause = (): void => {
    if (timer === null) {
      return;
    }

    clearTimeout(timer);
    timer = null;
    remaining = Math.max(0, remaining - (Date.now() - armedAt));
  };

  const onVisibilityChange = (): void => {
    if (isHidden()) {
      seenHidden = true;
      pause();
    } else {
      seenVisible = true;

      if (timer === null && remaining > 0) {
        arm();
      }
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  if (!isHidden()) {
    arm();
  }

  const visibility = (): TurnstileVisibility => {
    if (seenVisible && seenHidden) {
      return 'mixed';
    }

    return seenHidden ? 'hidden' : 'visible';
  };

  return {
    expired,
    timing: () => ({
      elapsedMs: Date.now() - startedAt,
      visibility: visibility(),
    }),
    dispose: () => {
      pause();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
};

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();

      return;
    }

    const script = document.createElement('script');

    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * @description Loads the script, or says why it could not before the clock ran out: an ad blocker
 * or a filtering proxy blocking challenges.cloudflare.com shows up as one or the other.
 * @param {VisibleClock} clock
 * @returns {Promise<string | null>} null once loaded, the reason otherwise
 */
const loadScriptWithin = (clock: VisibleClock): Promise<string | null> =>
  Promise.race([
    loadScript().then(
      () => null,
      () => 'script_blocked',
    ),
    clock.expired.then(() => 'script_timeout'),
  ]);

/**
 * @description Runs one challenge in a hidden container, and resolves once it has a token, a
 * failure no retry can fix, or no time left. When the time runs out, the widget keeps going for
 * a while if `onLateToken` wants what it may still produce.
 * @param {TurnstileApi} turnstile
 * @param {string} sitekey
 * @param {VisibleClock} clock
 * @param {TurnstileOptions['onLateToken']} [onLateToken]
 * @returns {Promise<TurnstileResult>}
 */
const solve = (
  turnstile: TurnstileApi,
  sitekey: string,
  clock: VisibleClock,
  onLateToken?: TurnstileOptions['onLateToken'],
): Promise<TurnstileResult> => {
  const container = document.createElement('div');

  container.style.display = 'none';
  document.body.appendChild(container);

  return new Promise<TurnstileResult>((resolve) => {
    let settled = false;
    let removed = false;
    let lastError: string | null = null;
    let lateTimer: ReturnType<typeof setTimeout> | null = null;

    const remove = (): void => {
      if (removed) {
        return;
      }

      removed = true;

      if (lateTimer !== null) {
        clearTimeout(lateTimer);
      }

      clock.dispose();

      try {
        turnstile.remove(container);
      } catch (error) {
        // The widget may already be gone; the container is removed either way.
      }
      container.remove();
    };

    /**
     * @param {string | null} token
     * @param {string} [error] Why there is no token
     * @param {boolean} [keepGoing] Leave the widget running for a late token
     */
    const settle = (
      token: string | null,
      error = 'empty_token',
      keepGoing = false,
    ): void => {
      if (settled) {
        return;
      }

      settled = true;

      const timing = clock.timing();

      resolve(
        token ? { token, error: null, timing } : { token: null, error, timing },
      );

      if (keepGoing && onLateToken) {
        lateTimer = setTimeout(remove, LATE_TOKEN_WINDOW_MS);
      } else {
        remove();
      }
    };

    /** Ends the challenge for good: settles it if nobody has been answered yet. */
    const giveUp = (error: string): void => {
      if (settled) {
        remove();
      } else {
        settle(null, error);
      }
    };

    const onToken = (token: string): void => {
      if (!settled) {
        settle(token);

        return;
      }

      if (token && !removed && onLateToken) {
        const timing = clock.timing();

        remove();
        onLateToken(token, timing);
      }
    };

    /**
     * Cloudflare keeps retrying a failed challenge on its own, so an error is not the end of it: the
     * clock is. When it runs out, the last error Cloudflare raised is the best account of why.
     */
    clock.expired.then(() => settle(null, lastError || 'timeout', true));

    try {
      turnstile.render(container, {
        sitekey,
        action: ACTION,
        retry: 'auto',
        'retry-interval': RETRY_INTERVAL_MS,
        callback: onToken,
        'error-callback': (errorCode) => {
          lastError = String(errorCode);

          if (FINAL_ERRORS.includes(lastError)) {
            giveUp(lastError);
          }

          // Otherwise not the end of it: Cloudflare retries on its own (`retry: 'auto'`).
          return true;
        },
        /**
         * A widget configured as "Managed" can decide to ask the visitor to click. Rendered into a
         * hidden container nobody can click, it would only run out the clock. Fail at once instead,
         * and say so: the widget should be configured as Invisible.
         */
        'before-interactive-callback': () => giveUp('interaction_required'),
        'timeout-callback': () => giveUp('interaction_timeout'),
        'unsupported-callback': () => giveUp('unsupported'),
      });
    } catch (error) {
      giveUp('render_failed');
    }
  });
};

/**
 * @description Solves a Turnstile challenge and returns the resulting token, or why there is none,
 * along with how long it took and whether the page was visible meanwhile.
 *
 * Rendered explicitly into a detached container rather than through markup: the challenge guards
 * the session the app mints on its own, not a form the visitor submits.
 *
 * @param {TurnstileOptions} [options]
 * @returns {Promise<TurnstileResult>}
 */
export default async function getTurnstileToken({
  onLateToken,
}: TurnstileOptions = {}): Promise<TurnstileResult> {
  // Captured locally: narrowing an imported binding does not survive into the callbacks below.
  const sitekey = TURNSTILE_SITEKEY;
  const clock = createVisibleClock(SOLVE_TIMEOUT_MS);

  const fail = (error: string): TurnstileResult => {
    const timing = clock.timing();

    clock.dispose();

    return { token: null, error, timing };
  };

  if (!sitekey) {
    return fail('no_sitekey');
  }

  const loadFailure = await loadScriptWithin(clock);

  if (loadFailure) {
    return fail(loadFailure);
  }

  const { turnstile } = window;

  if (!turnstile) {
    return fail('no_api');
  }

  return solve(turnstile, sitekey, clock, onLateToken);
}
